import { useMemo } from 'react';
import { useAuth } from 'react-oidc-context';

import type { WorkforceSession } from '@/types/catalog';

/**
 * Keycloak's full-path group claim carries a leading slash (`/app/gitlab/access`,
 * `/app/grafana-PRODUCTION/admin-access`), while catalog prefixes are slash-less.
 * Normalise at the boundary so entitlement matching can use `startsWith`.
 */
const toGroupPaths = (value: unknown): string[] => {
  const entries = typeof value === 'string' ? [value] : Array.isArray(value) ? value : [];
  return entries.filter((entry): entry is string => typeof entry === 'string').map((path) => path.replace(/^\//, ''));
};

/**
 * Projects the authenticated OIDC user onto the `WorkforceSession` shape the
 * dashboard already consumes. `groups` is the full-path Keycloak group claim
 * (e.g. `app/gitlab/developers`) that entitlement filtering reads. Returns
 * `null` until a user is present, so callers must render behind `RequireAuth`.
 */
export const useWorkforceSession = (): WorkforceSession | null => {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    const profile = user.profile;
    const claims = profile as Record<string, unknown>;

    return {
      name: profile.name ?? profile.preferred_username ?? profile.email ?? 'Unknown user',
      email: profile.email ?? '',
      groups: toGroupPaths(claims.groups ?? claims.group),
      authTime: typeof profile.auth_time === 'number' ? profile.auth_time : Math.floor(Date.now() / 1000),
    };
  }, [user]);
};
